import type {PropertyDefinitionBase, PropertyUsagesResponse, TargetLiterals} from '@github-ui/custom-properties-types'
import {useMutation, useQuery} from '@github-ui/react-query'
import sudo from '@github-ui/sudo'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {Dialog, type DialogButtonProps} from '@primer/react'
import {type ReactNode, type RefObject, useState} from 'react'

import {DefinitionInTargetsBanner} from './DefinitionInTargetsBanner'
import {ServerErrorFormBanner} from './ServerErrorFormBanner'

type Props = {
  definition: PropertyDefinitionBase
  deletePath: string
  usagePath: string
  targetLiterals: TargetLiterals
  returnFocusRef?: RefObject<HTMLElement | null>
  renderUsageBanner?: (usages?: PropertyUsagesResponse) => ReactNode
  onCancel: () => void
  onSuccess: () => void
}

export function DeleteDefinitionDialog({
  definition,
  deletePath,
  usagePath,
  targetLiterals,
  returnFocusRef,
  renderUsageBanner,
  onCancel,
  onSuccess,
}: Props) {
  const [deleting, setDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const deleteMutation = useMutation({
    mutationFn: deleteDefinition,
    onMutate: async path => {
      setDeleting(true)
      setErrorMessage('')

      if (!(await sudo({target: path, method: 'DELETE'}))) throw new Error('Unauthorized')
    },
    onSuccess,
    onError: error => {
      setErrorMessage(error.message)
      setDeleting(false)
    },
  })

  const {
    data: usages,
    isLoading: usagesLoading,
    error: usagesError,
  } = useQuery<PropertyUsagesResponse>({
    queryKey: ['definitions-usage-results', definition.propertyName, usagePath],
    queryFn: async () => checkPropertyUsage(usagePath),
  })

  // Escape focus trap when sudo dialog is open
  if (deleting) return null

  return (
    <Dialog
      width="large"
      onClose={onCancel}
      title="Delete property"
      returnFocusRef={returnFocusRef}
      renderBody={() => (
        <>
          {errorMessage && (
            <div aria-live="polite" className="tmp-pl-3 tmp-pr-3 pt-2">
              <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
            </div>
          )}
          <div>
            <div className="tmp-p-3">
              {usagesLoading ? (
                <span>Checking usages...</span>
              ) : (
                <>
                  <DefinitionInTargetsBanner
                    propertyName={definition.propertyName}
                    count={usages?.count}
                    targetLiterals={targetLiterals}
                    errorMessage={usagesError?.message}
                  />
                  {renderUsageBanner && <div className="tmp-pt-3">{renderUsageBanner(usages)}</div>}
                </>
              )}
            </div>
            <div className="tmp-pl-3 tmp-pr-3 tmp-pb-3">
              <span>
                This will permanently delete this property. <strong>This action cannot be undone.</strong>
              </span>
            </div>
          </div>
        </>
      )}
      footerButtons={[
        {
          onClick: onCancel,
          content: 'Cancel',
        },
        ...(!usagesLoading
          ? [
              {
                buttonType: 'danger',
                onClick: () => deleteMutation.mutate(deletePath),
                content: deleting ? 'Deleting...' : 'Delete',
                'aria-disabled': deleting,
              } as DialogButtonProps,
            ]
          : []),
      ]}
    />
  )
}

const deleteDefinition = async (path: string) => {
  const result = await reactFetchJSON(path, {method: 'DELETE'})
  if (result.ok) return

  throw new Error(`Something went wrong.`)
}

const checkPropertyUsage = async (usagePath: string) => {
  const result = await reactFetchJSON(usagePath)
  if (result.ok) return await result.json()

  throw new Error(`Failed to load property usage.`)
}
